import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';
import { optimizedApi } from '../utils/apiOptimizer';
import AccountDropdown from './AccountDropdown';
import UserSettingsModal from './UserSettingsModal';
import SubscriptionModal from './SubscriptionModal';
import FeedbackModal from './FeedbackModal';

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const { currentUser, userProfile, refreshUserProfile } = useAuth();
  const { isFree, loading: _planLoading, planData } = useUserPlan();
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const navLinksRef = useRef<{ [key: string]: HTMLAnchorElement | null }>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
    
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if clicking on the profile button or dropdown
      const target = event.target as HTMLElement;
      const profileButton = target.closest('[data-profile-button]');
      const dropdown = target.closest('[data-account-dropdown]');
      
      if (!profileButton && !dropdown) {
        setIsAccountOpen(false);
      }
    };

    if (isAccountOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isAccountOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const navItems = currentUser ? [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/upload', label: 'Clipper' },
    { path: '/downloads', label: 'Downloader' },
    { path: '/feedback', label: 'Feedback' },
  ] : [
    { path: '/upload', label: 'Clipper' },
    { path: '/downloads', label: 'Downloader' },
    { path: '/feedback', label: 'Feedback' },
  ];

  // Handle hover to prefetch analytics data
  const handleAnalyticsHover = () => {
    if (currentUser) {
      // Prefetch default analytics data (30 days) when user hovers
      optimizedApi.prefetch.analyticsData('30');
    }
  };

  // Update underline position when route changes
  useEffect(() => {
    const updateUnderlinePosition = () => {
      // Find the active link from our refs
      const activePath = location.pathname;
      const activeLink = navLinksRef.current[activePath];
      
      if (activeLink) {
        // Get the parent container's padding/position
        const parent = activeLink.parentElement;
        
        if (parent) {
          // Calculate position relative to the parent using offsetLeft
          // This accounts for all spacing including flex gaps
          const left = activeLink.offsetLeft;
          const width = activeLink.offsetWidth;
          
          setUnderlineStyle({
            left: left,
            width: width,
          });
        }
      } else {
        // Hide underline if no active link
        setUnderlineStyle({ left: 0, width: 0 });
      }
    };

    // Use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      updateUnderlinePosition();
    });
    
    // Update on window resize
    window.addEventListener('resize', updateUnderlinePosition);
    return () => window.removeEventListener('resize', updateUnderlinePosition);
  }, [location.pathname, currentUser]);

  return (
    <>
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-primary-100">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to={currentUser ? "/dashboard" : "/"} className="flex items-center group">
              <span className="text-2xl font-display font-bold tracking-tighter transition-all duration-300 hover:tracking-tight">
                CLIPTHIS
              </span>
              <div className="ml-2 w-2 h-2 bg-black rounded-full group-hover:animate-pulse" />
            </Link>
                      
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {currentUser ? (
                <>
                  <div className="relative nav-links-container">
                    <div ref={containerRef} className="flex items-center space-x-8">
                      {navItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          ref={(el) => (navLinksRef.current[item.path] = el)}
                          className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                          onMouseEnter={item.path === '/analytics' ? handleAnalyticsHover : undefined}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    {/* Sliding underline */}
                    <div
                      className="absolute h-[2px] bg-black transition-all duration-300 ease-out pointer-events-none"
                      style={{
                        left: `${underlineStyle.left}px`,
                        width: `${underlineStyle.width}px`,
                        bottom: '0px',
                      }}
                    />
                  </div>
                  
                  {/* Upgrade Button for Free Users */}
                  {currentUser && isFree && (userProfile || planData) && (
                    <button
                      onClick={() => setIsSubscriptionOpen(true)}
                      className="group relative inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-all duration-200 shadow-sm hover:shadow-md overflow-hidden mb-[6px]"
                    >
                      {/* Subtle gradient overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out" />
                      
                      <Crown size={16} className="mr-2 text-white group-hover:text-red-100 transition-colors" />
                      <span className="relative font-medium">Upgrade to Pro</span>
                      
                      {/* Small sparkle effect */}
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full animate-pulse opacity-75 group-hover:opacity-100 transition-opacity" />
                    </button>
                  )}
                          
                  {/* Account Button */}
                  <div className="relative mb-[6px]">
                    <button
                      data-profile-button
                      onClick={(e) => {
                        e.stopPropagation();
                        const willOpen = !isAccountOpen;
                        setIsAccountOpen(willOpen);
                        
                        // Refresh user profile when opening dropdown for latest data
                        if (willOpen) {
                          // console.log('🔄 [Navbar] Refreshing user profile when opening account dropdown');
                          refreshUserProfile();
                        }
                      }}
                      className="p-2 hover:bg-primary-50 rounded-lg transition-colors flex items-center"
                    >
                      {currentUser.photoURL ? (
                        <img
                          src={currentUser.photoURL}
                          alt="Profile"
                          className="w-8 h-8 rounded-md object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-8 h-8 bg-black text-white rounded-md flex items-center justify-center text-sm font-medium ${currentUser.photoURL ? 'hidden' : ''}`}>
                        {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : <User size={16} />}
                      </div>
                    </button>
                              
                    <AccountDropdown
                      isOpen={isAccountOpen}
                      onClose={() => setIsAccountOpen(false)}
                      onOpenSettings={() => {
                        setIsAccountOpen(false);
                        setIsSettingsOpen(true);
                      }}
                      onOpenSubscription={() => {
                        setIsAccountOpen(false);
                        setIsSubscriptionOpen(true);
                      }}
                      onOpenFeedback={() => {
                        setIsAccountOpen(false);
                        setIsFeedbackOpen(true);
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="relative nav-links-container">
                    <div ref={containerRef} className="flex items-center space-x-8">
                      {navItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          ref={(el) => (navLinksRef.current[item.path] = el)}
                          className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                    {/* Sliding underline */}
                    <div
                      className="absolute h-[2px] bg-black transition-all duration-300 ease-out pointer-events-none"
                      style={{
                        left: `${underlineStyle.left}px`,
                        width: `${underlineStyle.width}px`,
                        bottom: '0px',
                      }}
                    />
                  </div>
                  <Link to="/login" className="nav-link mb-[6px]">
                    Sign In
                  </Link>
                  <Link to="/signup" className="btn-primary text-xs mb-[6px]">
                    Get Started
                  </Link>
                </>
              )}
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 hover:bg-primary-50 rounded-lg transition-colors"
              aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation */}
        <div className={`md:hidden absolute top-16 left-0 w-full bg-white border-b border-primary-100 transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}>
          <div className="px-6 py-4 space-y-3">
            {currentUser ? (
              <>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    onMouseEnter={item.path === '/analytics' ? handleAnalyticsHover : undefined}
                    className={`block py-2 text-sm font-medium ${
                      isActive(item.path) ? 'text-black' : 'text-primary-600'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                
                {/* Mobile Upgrade Button for Free Users */}
                {currentUser && isFree && (userProfile || planData) && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsSubscriptionOpen(true);
                    }}
                    className="group relative w-full inline-flex items-center justify-center px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-all duration-200 shadow-sm overflow-hidden"
                  >
                    {/* Subtle gradient overlay on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out" />
                    
                    <Crown size={16} className="mr-2 text-white group-hover:text-red-100 transition-colors" />
                    <span className="relative font-medium">Upgrade to Pro</span>
                    
                    {/* Small sparkle effect */}
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-400 rounded-full animate-pulse opacity-75 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
              </>
            ) : (
              <>
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`block py-2 text-sm font-medium ${
                      isActive(item.path) ? 'text-black' : 'text-primary-600'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-2 text-sm font-medium text-primary-600"
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMenuOpen(false)}
                  className="btn-primary w-full text-xs mt-4"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>
      
      {/* Modals */}
      <UserSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onOpenSubscription={() => {
          setIsSettingsOpen(false);
          setIsSubscriptionOpen(true);
        }}
      />
      
      <SubscriptionModal
        isOpen={isSubscriptionOpen}
        onClose={() => setIsSubscriptionOpen(false)}
      />
      
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
    </>
  );
};

export default Navbar; 